import React, { useState, useMemo, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import SortBySelect from "../../dashboard-shared/sort-by-select-dropdown/sort-by-select";
import Modal from "react-bootstrap/Modal";
import "./active-strs-bar-chart.scss";
import PropTypes from "prop-types";
import {
  getRandomColors,
  categoryDisplayMappings,
  reverseCategoryMappings,
} from "../../dashboard-util/dashboard-utils";
import FluentUiIcon from "../../../../assets/fluent-ui-icon";
import { FluentButton } from "../../../../components/FluentUI/Fluent-Button";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

const ActiveSTRsBarChart = ({ data }) => {

  const storedCategory =
    localStorage.getItem("activeSTRs_category") || "Status";

  const [selectedCategory, setSelectedCategory] = useState(storedCategory);
  const [modalVisibility, setModalVisibility] = useState(false);

  useEffect(() => {
    const persistedCategory = localStorage.getItem("activeSTRs_category");
    if (persistedCategory && persistedCategory !== "undefined") {
      setSelectedCategory(persistedCategory);
    }
  }, []);

  const categoryMappings = useMemo(
    () => ({
      Status: {
        labels: [
          "1st Level Triage",
          "Not Started",
          "In Progress",
          "Pending Compliance Review",
          "Approved for Filing",
        ],
        colors: ["#C8C6C4", "#FFBF15", "#2B8FDE", "#00809D", "#00AA58"],
        data: [
          data.Status.InitialTriageCount,
          data.Status.NotStartedCount,
          data.Status.InProgressCount,
          data.Status.PendingComplianceReviewCount,
          data.Status.ApprovedForFilingCount
        ],
      },
      Priority: {
        labels: ["Low", "Medium", "High", "Urgent"],
        colors: ["#13B5EA", "#FFBF15", "#D4424F", "#73242B"],
        data: [
          data.Priority.LowCount,
          data.Priority.MediumCount,
          data.Priority.HighCount,
          data.Priority.UrgentCount,
        ],
      },
      TriggerSource: {
        labels: ["STR Alert Event", "Navaera Event", "Manual"],
        colors: ["#EF858E", "#8764B8", "#0974EB"],
        data: [
          data.TriggerSource.STRAlertEventCount,
          data.TriggerSource.NavaeraEventCount,
          data.TriggerSource.ManualCount,
        ],
      },
      Department: {
        labels: ["Branch", "Operational Risk", "Other", "N/A"],
        colors: ["#287EC7", "#AD3640", "#C8C6C4", "#555557"],
        data: [
          data.Department.BranchCount,
          data.Department.OperationalRiskCount,
          data.Department.OtherCount,
          data.Department.NACount,
        ],
      },
      BranchLocation: {
        labels: data.BranchLocation.Locations.map((loc) => loc.LocationName),
        colors: getRandomColors(data.BranchLocation.Locations.length),
        data: data.BranchLocation.Locations.map((loc) => loc.Count),
      },
      TriggerType: {
        labels: [
          "Production Order",
          "High Risk Review",
          "Victim of Fraud",
          "Owner Behavior",
          "Transaction Monitoring",
          "Re-occurring STR",
        ],
        colors: [
          "#086BD8",
          "#AF251F",
          "#A38F69",
          "#429541",
          "#00809D",
          "#EAAA00",
        ],
        data: [
          data.TriggerType.ProductionOrderCount,
          data.TriggerType.HighRiskCount,
          data.TriggerType.VictimOfFraudCount,
          data.TriggerType.OwnerBehaviorCount,
          data.TriggerType.TransactionMonitoringCount,
          data.TriggerType.ReoccuringSTRCount,
        ],
      },
    }),
    [data],
  );

  const handleCategoryChange = (selectedDisplayName) => {
    const categoryKey = reverseCategoryMappings[selectedDisplayName];
    setSelectedCategory(categoryKey);
    localStorage.setItem("activeSTRs_category", categoryKey);
  };

  const chartData = {
    labels: categoryMappings[selectedCategory].labels,
    datasets: [
      {
        data: categoryMappings[selectedCategory].data,
        backgroundColor: categoryMappings[selectedCategory].colors,
        borderColor: categoryMappings[selectedCategory].colors,
        borderWidth: 1,
        borderRadius: 4,
        categoryPercentage: 0.6,
        barPercentage: 0.7,
        maxBarThickness: 50,
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#333", font: { size: 12 } },
      },
      y: {
        beginAtZero: true,
        grace: "15%",
        ticks: { color: "#333", font: { size: 12 } },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        fullSize: true,
        labels: {
          boxWidth: 14,
          color: "#333333",
          font: { size: 14, weight: 500 },
          generateLabels: (chart) => {
            const ds = chart.data.datasets[0];
            const labels = chart.data.labels || [];

            return labels.map((label, index) => {
              const value = ds.data[index] || 0;
              return {
                text: `${label}: ${value}`,
                fillStyle: ds.backgroundColor[index],
                strokeStyle: ds.backgroundColor[index],
                lineWidth: 1,
                hidden: !chart.getDataVisibility(index),
                index,
              };
            });
          },
        },
        onClick: (e, legendItem, legend) => {
          const index = legendItem.index;
          const chart = legend.chart;
          const visible = chart.getDataVisibility(index);
          chart.toggleDataVisibility(index, !visible);
          chart.update();
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: "#777777",
        callbacks: {
          label(tooltipItem) {
            const val = tooltipItem.parsed.y ?? tooltipItem.parsed;
            const data = tooltipItem.chart.data.datasets[0].data;
            const total = data.reduce((acc, v) => acc + (v || 0), 0);
            const pct = total ? ((val / total) * 100).toFixed(1) : "0.0";
            return ` ${tooltipItem.label}: ${val} (${pct}%)`;
          },
        },
      },
      datalabels: {
        display: "auto",
        color: "#333",
        font: { size: 12, weight: "bold" },
        anchor: "end",
        align: "top",
        offset: 6,
        clamp: true,
        formatter: (value) => (value > 0 ? value : ""),
      },
    },
  };

  const handleModalShow = () => setModalVisibility(true);
  const handleModalClose = () => setModalVisibility(false);

  return (
    <div className="active-strs-doughnut-chart-container">
      <div className="active-str-doughnut-chart-header">
        <div className="active-str-doughnut-header-left">
          <h4 className="active-str-doughnut-panel-title">Active STRs by&nbsp;</h4>
          <SortBySelect
            value={categoryDisplayMappings[selectedCategory]}
            handleSelect={handleCategoryChange}
            options={Object.values(categoryDisplayMappings)}
          />
        </div>
        <FluentButton
          appearance="none"
          onClick={handleModalShow}
          icon={<FluentUiIcon name="ArrowMaximizeRegular" color="#0F6CBD" size={20} />}
        />
      </div>

      <div className="wrapper">
        <Bar data={chartData} options={chartOptions} plugins={[ChartDataLabels]} />
      </div>

      <Modal
        show={modalVisibility}
        onHide={handleModalClose}
        dialogClassName="active-tasks-modal-dialog"
        contentClassName="active-tasks-modal-styles"
      >
        <Modal.Header className="p-0 w-100">
          <div className="active-str-doughnut-modal-header">
            <div className="active-str-doughnut-header-left">
              <h4 className="active-str-doughnut-panel-title">Active STRs by&nbsp;</h4>
              <SortBySelect
                value={categoryDisplayMappings[selectedCategory]}
                handleSelect={handleCategoryChange}
                options={Object.values(categoryDisplayMappings)}
              />
            </div>
            <button
              className="active-tasks-exit-modal header-button"
              onClick={handleModalClose}
            />
          </div>
        </Modal.Header>
        <Modal.Body>
          <Bar data={chartData} options={chartOptions} plugins={[ChartDataLabels]} />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ActiveSTRsBarChart;

ActiveSTRsBarChart.propTypes = {
  data: PropTypes.shape({
    Status: PropTypes.shape({
      InitialTriageCount: PropTypes.number.isRequired,
      NotStartedCount: PropTypes.number.isRequired,
      InProgressCount: PropTypes.number.isRequired,
      ApprovedForFilingCount: PropTypes.number.isRequired,
      PendingComplianceReviewCount: PropTypes.number.isRequired
    }).isRequired,
    Priority: PropTypes.shape({
      LowCount: PropTypes.number.isRequired,
      MediumCount: PropTypes.number.isRequired,
      HighCount: PropTypes.number.isRequired,
      UrgentCount: PropTypes.number.isRequired,
    }).isRequired,
    TriggerSource: PropTypes.shape({
      STRAlertEventCount: PropTypes.number.isRequired,
      NavaeraEventCount: PropTypes.number.isRequired,
      ManualCount: PropTypes.number.isRequired,
    }).isRequired,
    Department: PropTypes.shape({
      BranchCount: PropTypes.number.isRequired,
      OperationalRiskCount: PropTypes.number.isRequired,
      OtherCount: PropTypes.number.isRequired,
      NACount: PropTypes.number.isRequired,
    }).isRequired,
    BranchLocation: PropTypes.shape({
      Locations: PropTypes.arrayOf(
        PropTypes.shape({
          LocationName: PropTypes.string.isRequired,
          Count: PropTypes.number.isRequired,
        })
      ).isRequired,
    }).isRequired,
    TriggerType: PropTypes.shape({
      ProductionOrderCount: PropTypes.number.isRequired,
      HighRiskCount: PropTypes.number.isRequired,
      VictimOfFraudCount: PropTypes.number.isRequired,
      OwnerBehaviorCount: PropTypes.number.isRequired,
      TransactionMonitoringCount: PropTypes.number.isRequired,
      ReoccuringSTRCount: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
};